import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { useMemo } from 'react';
import { ListingResource, useListingField } from '@brightmls/nextgen.lae.flc';
import {
  Documents,
  MediaCenter as MediaCenterComponent,
  MediaCenterProvider,
  Photos,
  type ResourceFields,
  Tours,
} from '@brightmls/nextgen.lae.mediacenter';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import { useTenantPath } from '../../../hooks/useTenantPath';
import { Permissions } from '../../../system/permission';
import { MediaCenterDataLoader } from './MediaCenterDataLoader';
import { useMediaCenterConfig } from './config/useMediaCenterConfig';
import { useMediaHandlers } from './handlers/useMediaHandlers';
import { ProtectedRoute } from './components/ProtectedRoute';
import { PropertyField } from '../../../system/field';
import { ContentMinHeight } from '../../../components/common/ContentMinHeight';
import { useWebSocketContext } from '../../../contexts/WebSocketContext';
import { useMediaLogger } from './hooks/useMediaLogger';

export const MediaCenterWrapper = () => {
  const navigate = useNavigate();

  const { allowed } = useAuth();
  const { supportsClientResoWebApi } = useTenant();
  const { getPath } = useTenantPath();
  const { listingKey } = useParams<{ listingKey: string }>();
  const { connectionId } = useWebSocketContext();
  const logger = useMediaLogger('MediaCenterWrapper');

  // Create a field cache Map that will be populated with FLC field states
  const fieldCache = useMemo(() => new Map<string, any>(), []);

  // Populate field cache with fields needed by MediaCenter
  const autoHideField = useListingField(
    ListingResource.Property,
    PropertyField.AUTOMATICALLY_HIDE_MEDIA_OFF_MARKET_YN,
    false,
  );
  const visibleOffMarketKeysField = useListingField(
    ListingResource.Property,
    PropertyField.VISIBLE_WHEN_OFF_MARKET_MEDIA_KEYS,
    [] as number[],
  );

  // Get listingId for AI processing optimization
  const listingIdField = useListingField(
    ListingResource.Property,
    PropertyField.LISTING_ID,
    '',
  );
  const listingId = listingIdField?.value as string | undefined;

  // Add fields to cache
  fieldCache.set('AUTOMATICALLY_HIDE_MEDIA_OFF_MARKET_YN', autoHideField);
  fieldCache.set(
    'VISIBLE_WHEN_OFF_MARKET_MEDIA_KEYS',
    visibleOffMarketKeysField,
  );
  fieldCache.set(PropertyField.LISTING_ID, listingIdField);

  // Resource adapter that uses FLC's field system
  const resource: ResourceFields = useMemo(
    () => ({
      get: (fieldName: string) => {
        // Check if field is in cache
        if (fieldCache.has(fieldName)) {
          return fieldCache.get(fieldName);
        }

        // Return a basic field state if not cached
        return {
          value: undefined,
          isDirty: false,
          isValid: true,
          errors: [],
          warnings: [],
          isHidden: false,
          isRequired: false,
          addToList: [],
          removeFromList: [],
          renderCount: 0,
          hasErrors: false,
          hasWarnings: false,
          isDisabled: false,
          updateValue: (value: any) => {
            logger.log(`Update field ${fieldName}:`, value);
          },
        };
      },
      set: (fieldName: string, value: any) => {
        const field = fieldCache.get(fieldName);
        if (field?.updateValue) {
          field.updateValue(value);
        } else {
          logger.log(`Set field ${fieldName}:`, value);
        }
      },
    }),
    [fieldCache, logger],
  );

  const config = useMediaCenterConfig(listingKey!, resource);
  const handlers = useMediaHandlers(listingKey!, { connectionId, listingId });

  const isResoSystem = supportsClientResoWebApi();
  const isAddEditPhotosAllowed = allowed({
    permission: Permissions.UPLOAD_LISTING_PHOTOS,
  });
  const isAddEditDocumentsAllowed = allowed({
    permission: Permissions.UPLOAD_DOCUMENTS,
  });
  const isAddEditToursAllowed = allowed({
    permission: Permissions.ADD_EDIT_VIRTUAL_TOURS,
  });

  const tabs = [
    {
      path: config.routing?.paths?.photos || 'photos',
      label: 'Photos',
    },
    {
      path: config.routing?.paths?.documents || 'documents',
      label: 'Documents',
    },
  ];

  if (isResoSystem) {
    tabs.push({
      path: config.routing?.paths?.tours || 'tours',
      label: 'Virtual Tours',
    });
  }

  return (
    <ContentMinHeight>
      <Routes>
        <Route
          path="*"
          element={
            <MediaCenterProviderWrapper
              onBack={() =>
                navigate(getPath(`/edit/${listingKey}`), {
                  state: { from: 'media' },
                })
              }
              config={config}
              handlers={handlers}
              isResoSystem={isResoSystem}
              isAddEditPhotosAllowed={isAddEditPhotosAllowed}
              isAddEditDocumentsAllowed={isAddEditDocumentsAllowed}
              isAddEditToursAllowed={isAddEditToursAllowed}
              mediaTypeTabs={tabs}
            />
          }
        />
      </Routes>
    </ContentMinHeight>
  );
};

// New wrapper component that's rendered INSIDE a Route
interface MediaCenterProviderWrapperProps {
  config: any;
  handlers: any;
  onBack: () => void;
  mediaTypeTabs?: Array<{ path: string; label: string }>;
  isResoSystem: boolean;
  isAddEditPhotosAllowed: boolean;
  isAddEditDocumentsAllowed: boolean;
  isAddEditToursAllowed: boolean;
}

function MediaCenterProviderWrapper({
  config,
  handlers,
  mediaTypeTabs,
  isResoSystem,
  onBack,
  isAddEditPhotosAllowed,
  isAddEditDocumentsAllowed,
  isAddEditToursAllowed,
}: MediaCenterProviderWrapperProps) {
  return (
    <MediaCenterProvider config={config} {...handlers}>
      <MediaCenterDataLoader>
        <Routes>
          <Route
            element={
              <MediaCenterComponent
                onBack={onBack}
                mediaTypes={mediaTypeTabs}
              />
            }
          >
            <Route index element={<Navigate to="photos" replace />} />
            <Route
              path="photos"
              element={
                <ProtectedRoute allowed={isAddEditPhotosAllowed}>
                  <Photos />
                </ProtectedRoute>
              }
            />
            <Route
              path="documents"
              element={
                <ProtectedRoute allowed={isAddEditDocumentsAllowed}>
                  <Documents />
                </ProtectedRoute>
              }
            />
            {isResoSystem && (
              <Route
                path="tours"
                element={
                  <ProtectedRoute allowed={isAddEditToursAllowed}>
                    <Tours />
                  </ProtectedRoute>
                }
              />
            )}
          </Route>
        </Routes>
      </MediaCenterDataLoader>
    </MediaCenterProvider>
  );
}
